/**
 * Gitea as a git provider — a self-hosted Gitea, and the servers that speak
 * its API: Forgejo and Codeberg (owner, 07.10.2026: "the + should support
 * Gitea … for the users that want to stay local without an account").
 *
 * Sign-in with a personal access token (Settings → Applications; scopes
 * repository + user read/write). Accounts live in services/git/gitAccounts.js
 * like GitLab's: the token in the OS keychain, never in a file, never sent to
 * Apiboo. A workspace link names its account (`git.accountId`).
 *
 * API v1 through the Tauri HTTP adapter (no CORS). Requests go only to the
 * server the account was added for. Checked against Gitea 1.22 in Docker
 * (07.10.2026): `/user`, `/user/repos`, `/repos/search?topic=true`, topics,
 * and a push with the token under the HTTPS user "oauth2".
 */
import axios from 'axios';
import { tauriAxiosAdapter } from '../../../utils/tauriAxiosAdapter';
import { WORKSPACE_TOPIC } from '../../../utils/appInfo';
import { getGitAccount, getGitAccountToken, listGitAccounts, hostOf } from '../gitAccounts';
import { mergeRepos, ownersOf } from '../repoCatalog';

const PAGE = 50; // Gitea's default maximum page size
const MAX_PAGES = 4;

function client(server, token) {
  return axios.create({
    baseURL: `${server}/api/v1`,
    adapter: tauriAxiosAdapter,
    timeout: 15000,
    headers: { Authorization: `token ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' },
  });
}

/** The account a link (or a picker choice) points at; the only Gitea account on that host, or the only one. */
async function accountFor(git) {
  if (git?.accountId) return getGitAccount(git.accountId);
  const all = await listGitAccounts('gitea');
  const host = git?.url ? hostOf(git.url) : '';
  return (host ? all.find((a) => hostOf(a.server) === host) : null) || (all.length === 1 ? all[0] : null);
}

async function api(git, token) {
  const account = await accountFor(git);
  if (!account) {
    const err = new Error('GITEA_NOT_LINKED');
    err.code = 'GITEA_NOT_LINKED';
    throw err;
  }
  return { account, http: client(account.server, token) };
}

function mapRepo(r, orgLogins) {
  const owner = r.owner?.login || '';
  return {
    fullName: r.full_name,
    name: r.name,
    owner,
    ownerKey: owner,
    ownerAvatar: r.owner?.avatar_url || '',
    ownerType: orgLogins?.has(owner.toLowerCase()) ? 'org' : 'user',
    private: Boolean(r.private),
    description: r.description || '',
    updatedAt: r.updated_at || '',
    url: r.clone_url,
    defaultBranch: r.default_branch || '',
  };
}

const newestFirst = (a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt));

/** Every page of a list endpoint, up to MAX_PAGES. `pick` reads the array out of a page. */
async function allPages(http, path, params, pick = (d) => d) {
  const out = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const { data } = await http.get(path, { params: { ...params, limit: PAGE, page } });
    const picked = pick(data);
    const items = Array.isArray(picked) ? picked : [];
    out.push(...items);
    if (items.length < PAGE) break;
  }
  return out;
}

const reEscape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const gitea = {
  id: 'gitea',
  label: 'Gitea',
  icon: 'gitea',
  authKinds: ['token'],
  baseUrl: '',
  serverPlaceholder: 'https://gitea.example.com',
  selfHosted: true,
  abilities: { createRepo: true, listRepos: true, listBranches: true, openInBrowser: true, deviceFlow: false, tagWorkspace: true },
  /** Gitea reads the token from the password and ignores the user name; "oauth2" is what GitLab uses too. */
  httpsUsername: 'oauth2',
  notLinkedCode: 'GITEA_NOT_LINKED',

  /** Where the user makes a token on this server. */
  tokenPage(server) {
    return server ? `${server}/user/settings/applications` : '';
  },

  /** Who a token belongs to — used when adding an account. */
  async verifyToken(server, token) {
    const { data } = await client(server, token).get('/user');
    return { login: data?.login, name: data?.full_name, avatarUrl: data?.avatar_url, userId: data?.id };
  },

  async getToken(git) {
    const account = await accountFor(git);
    return account ? getGitAccountToken(account.id) : '';
  },

  async getAccount(git) {
    const account = await accountFor(git);
    return account
      ? { connected: true, login: account.login, avatarUrl: account.avatarUrl, server: account.server, id: account.id, userId: account.userId }
      : { connected: false, login: '', avatarUrl: '' };
  },

  /** Gitea's default no-reply address: <login>@noreply.<host name> (no port). */
  authorEmail(login, account) {
    if (!login) return 'noreply@apiboo.com';
    let host = '';
    try {
      host = account?.server ? new URL(account.server).hostname : '';
    } catch {
      host = '';
    }
    return host ? `${login}@noreply.${host}` : 'noreply@apiboo.com';
  },

  async repoUrl(owner, repo, git) {
    const account = await accountFor(git);
    return account ? `${account.server}/${owner}/${repo}.git` : '';
  },

  webUrl(git) {
    return git?.url ? git.url.replace(/\.git$/, '') : '';
  },

  /** "owner/repo" or a URL on `server` → { owner, repo, url }. Gitea has no nested groups. */
  parseRepo(input, server) {
    const s = String(input || '').trim();
    if (!s || !server) return null;
    const toRef = (path) => {
      const parts = path.replace(/\.git$/, '').replace(/\/+$/, '').split('/').filter(Boolean);
      if (parts.length !== 2) return null;
      const [owner, repo] = parts;
      return { owner, repo, url: `${server}/${owner}/${repo}.git` };
    };
    if (!s.includes('://') && !s.includes('@')) return toRef(s);
    const base = server.replace(/^[a-z]+:\/\//i, '');
    const host = hostOf(server);
    // https://host[/sub-path]/owner/repo, or git@host:owner/repo
    const m = s.match(new RegExp(`${reEscape(base)}/(.+)$`, 'i')) || s.match(new RegExp(`@${reEscape(host.split(':')[0])}(?::\\d+)?[:/](.+)$`, 'i'));
    return m ? toRef(m[1].split(/[?#]/)[0]) : null;
  },

  /**
   * Create the repository if it is not there — with a README (auto_init), for
   * the same reason as GitHub and GitLab: a push into an EMPTY repo fails
   * under libgit2. Under the user, or under an organization they name.
   */
  async ensureRepoExists(token, owner, repo, isPrivate, git) {
    const { account, http } = await api(git, token);
    try {
      await http.get(`/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`);
      return true;
    } catch (e) {
      if (e.response?.status !== 404) throw e;
    }
    const body = { name: repo, private: Boolean(isPrivate), auto_init: true, default_branch: 'main' };
    const path = owner && owner !== account.login ? `/orgs/${encodeURIComponent(owner)}/repos` : '/user/repos';
    try {
      await http.post(path, body);
      return false;
    } catch (e) {
      if (e.response?.status === 409) return true; // the name is taken: it exists after all
      throw e;
    }
  },

  async listRepos(token, git) {
    return (await gitea.listRepoCatalog(token, git)).repos;
  },

  /**
   * { repos, owners, notices } (28.10.2026): `/user/repos`, then `/user/orgs`
   * and `/orgs/{org}/repos` per organization — merged, de-duplicated by full
   * name. A token without the `read:organization` scope cannot list the
   * organizations (403): the user's own repositories still list and the
   * picker says which scope is missing.
   */
  async listRepoCatalog(token, git) {
    const { account, http } = await api(git, token);
    const own = await allPages(http, '/user/repos', {});
    const notices = [];
    let orgs = [];
    try {
      orgs = await allPages(http, '/user/orgs', {});
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403 || status === 401) notices.push({ kind: 'missingScope', owner: '', scope: 'read:organization', tokenKind: 'gitea', url: gitea.tokenPage(account.server) });
      else notices.push({ kind: 'orgUnavailable', owner: '', status: status || 0 });
    }
    const orgLogins = new Set(orgs.map((o) => String(o?.username || o?.name || '').toLowerCase()).filter(Boolean));
    const lists = [own.map((r) => mapRepo(r, orgLogins))];
    for (const org of orgs) {
      const login = org?.username || org?.name;
      if (!login) continue;
      try {
        lists.push((await allPages(http, `/orgs/${encodeURIComponent(login)}/repos`, {})).map((r) => mapRepo(r, orgLogins)));
      } catch (e) {
        notices.push({ kind: 'orgUnavailable', owner: login, status: e?.response?.status || 0 });
      }
    }
    const repos = mergeRepos(...lists).sort(newestFirst);
    const owners = ownersOf(repos, {
      login: account.login || '',
      avatarUrl: account.avatarUrl || '',
      extra: orgs.map((o) => ({ key: o.username || o.name, login: o.username || o.name, avatarUrl: o.avatar_url || '', type: 'org' })),
    });
    return { repos, owners, notices };
  },

  /** A repository's branches: [{ name, protected }]. */
  async listBranches(token, owner, repo, git) {
    const { http } = await api(git, token);
    const list = await allPages(http, `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches`, {});
    return list.map((b) => ({ name: b.name, protected: Boolean(b.protected) }));
  },

  /** Only repositories carrying the workspace topic. */
  async listWorkspaceRepos(token, _login, git) {
    const { http } = await api(git, token);
    const list = await allPages(http, '/repos/search', { q: WORKSPACE_TOPIC, topic: true }, (d) => d?.data);
    return list.map((r) => mapRepo(r)).sort(newestFirst);
  },

  /** Add the workspace topic (keeps the repository's own). Best-effort — never throws. */
  async tagWorkspace(token, owner, repo, git) {
    try {
      const { http } = await api(git, token);
      const path = `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/topics`;
      const { data } = await http.get(path);
      const topics = Array.isArray(data?.topics) ? data.topics : [];
      if (topics.includes(WORKSPACE_TOPIC)) return;
      await http.put(path, { topics: [...topics, WORKSPACE_TOPIC] });
    } catch {
      // best-effort; tagging must not block sync
    }
  },
};

export default gitea;
